import React from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Vehicle } from "@amsanalytics/react-shared-library";
import { AppState } from "../store/rootReducer";
import { ApplicationDispatch, solveLeaseQuote } from "../store/application/types";
import { IApplication } from "../Interfaces/Application/IApplication";
import { LeaseCalcMode } from "../Interfaces/Application/ApplicationLeaseCalculationResult";
import { useDialog } from "../providers/DialogProvider";
import { useEmployeeRolePermissions } from "../Data/EmployeeRoleData";
import { usePostedAppLock } from "./usePostedAppLock";
import { validateLeaseQuote } from "../utils/leaseQuoteValidation";
import { getLeaseAutoSolve, getLeaseVehicleEditEffect } from "../utils/leaseQuoteSolveUtils";
import { useBusinessRulesEngine } from "../Data/BusinessRulesData";
import ErrorDialog from "../Components/Dialogs/ErrorDialog";
import {
  SellingPriceConfirmationDialog
} from "../Components/Views/OrderEditor/SellingPriceConfirmationDialog/SellingPriceConfirmationDialog";

const buttonLabel = (mode: LeaseCalcMode) => (mode === "RollBack" ? "Roll Back" : "Quote / Solve");

/** Lease Quote / Solve and Roll Back through the lease calc endpoint, gated like the finance quoter. */
export const useLeaseQuoteSolve = () => {
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const loading = useSelector((state: AppState) => state.applications.loading);
  const [, , , canActInStatus] = useEmployeeRolePermissions();
  const { isSectionLocked, activeUnlock } = usePostedAppLock();
  const dispatch = useDispatch<ApplicationDispatch>();
  const [openDialog, closeDialog] = useDialog();
  const store = useStore<AppState>();
  const businessRulesEngine = useBusinessRulesEngine();

  const canSolve = (application: IApplication) =>
    (canActInStatus(application.appStatusId ?? "", "Save") || !!activeUnlock) && !isSectionLocked("Quoter");

  const validate = (application: IApplication, mode: LeaseCalcMode, newVehicle: Vehicle | undefined) =>
    validateLeaseQuote(application.applicationLeaseQuote, {
      loanClass: lookups.leaseLoanClasses.find((lc) => lc.id === application.applicationLeaseQuote.leaseLoanClassId),
      hasVehicle: !!(newVehicle ?? application.vehicle),
      mode,
    });

  const withVehicle = (application: IApplication, newVehicle: Vehicle | undefined, sellingPrice: number | null): IApplication =>
    newVehicle
      ? {
          ...application,
          vehicleId: newVehicle.id,
          vehicle: newVehicle,
          applicationLeaseQuote: { ...application.applicationLeaseQuote, sellingPrice: sellingPrice ?? newVehicle.carPrice },
        }
      : application;

  const showErrors = (title: string, errors: string[]) =>
    openDialog({ children: <ErrorDialog title={title} errors={errors} closeDialog={closeDialog} /> });

  /** Quote / Solve or Roll Back the user asked for, with the finance quoter's dialogs. */
  const solve = (application: IApplication, mode: LeaseCalcMode, newVehicle?: Vehicle) => {
    const label = buttonLabel(mode);
    if (!canSolve(application)) {
      const reason = isSectionLocked("Quoter")
        ? "The Quoter section is locked because the application has been finalized and posted. An authorized user must unlock it before editing."
        : `The ${label} button is disabled for your user role(s) in this status`;
      showErrors(`${label} Disabled`, [reason]);
      return;
    }

    const errors = validate(application, mode, newVehicle);
    if (errors.length > 0) {
      showErrors(`${label} Validation Errors`, errors);
      return;
    }

    if (newVehicle && newVehicle.carPrice <= 0) {
      openDialog({
        children: (
          <SellingPriceConfirmationDialog
            sellingPriceFromSelectedVehicle={newVehicle.carPrice}
            onSave={(sellingPrice: number) => {
              dispatch(solveLeaseQuote(withVehicle(application, newVehicle, sellingPrice), mode, businessRulesEngine, newVehicle));
              closeDialog();
            }}
            onClose={closeDialog}
          />
        ),
      });
      return;
    }

    dispatch(solveLeaseQuote(withVehicle(application, newVehicle, null), mode, businessRulesEngine, newVehicle));
  };

  const autoSolveOf = (application: IApplication) =>
    getLeaseAutoSolve(application, store.getState().applications.leaseQuoteSolvedRollback);

  /**
   * Re-solves a solved quote after a vehicle or trade-in change, without dialogs. Returns false when it
   * could not run (not solved, locked, not allowed, invalid, or a Solve already running).
   */
  const resolveQuietly = (application: IApplication): boolean => {
    const autoSolve = autoSolveOf(application);
    if (loading || !autoSolve || !canSolve(application) || validate(autoSolve.application, autoSolve.mode, undefined).length > 0) {
      return false;
    }
    dispatch(solveLeaseQuote(autoSolve.application, autoSolve.mode, businessRulesEngine));
    return true;
  };

  /**
   * After a saved edit of the Lease app's vehicle changed its price or ACV, re-solves with the saved values.
   * A negotiated selling price the server replaced is left yellow for the user to check.
   */
  const resolveAfterVehicleEdit = (applicationBeforeSave: IApplication | undefined, vehicle: Vehicle) => {
    const effect = getLeaseVehicleEditEffect(applicationBeforeSave, vehicle);
    const latest = store.getState().applications.editingApp;
    if (latest && effect.needsSolve && !effect.negotiatedPriceReplaced) {
      resolveQuietly(latest);
    }
  };

  /**
   * Re-solves a solved quote with a newly picked vehicle (the server attaches it). Returns false when a
   * Solve cannot run, so the caller attaches the vehicle the usual way and the quote turns yellow.
   */
  const solveWithNewVehicle = (application: IApplication, vehicle: Vehicle): boolean => {
    const autoSolve = autoSolveOf(application);
    if (loading || !autoSolve || !canSolve(application) || validate(autoSolve.application, autoSolve.mode, vehicle).length > 0) {
      return false;
    }
    solve(autoSolve.application, autoSolve.mode, vehicle);
    return true;
  };

  return { solve, resolveQuietly, resolveAfterVehicleEdit, solveWithNewVehicle };
};
